/**
 * Discover's search results (#2290): one `POST api/models/query` per settled
 * search, then "load more" pages by `skip` while the server says `hasMore`.
 *
 * Results belong to the search they were asked for (`key`): a search that
 * changed while one was in flight shows as searching, never the old rows,
 * and a late answer (or a late page) for an older search is dropped. A 400
 * keeps the server's problems, in plain words, for the box to show.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { InvalidModelQueryError, MAX_QUERY_SKIP, queryModels, type ModelQuery } from '@/api/modelQuery';
import type { Model } from '@/types';
import { explainProblems } from './queryProblems';

export const SEARCH_PAGE_SIZE = 24;

interface Answer {
  key: string;
  attempt: number;
  results: Model[];
  hasMore: boolean;
  /** The server refused the query: why, in plain words. */
  problems: string[] | null;
  /** It failed for another reason. */
  failed: boolean;
}

export interface ModelSearch {
  results: Model[];
  hasMore: boolean;
  /** No answer yet for the current search. */
  searching: boolean;
  loadingMore: boolean;
  problems: string[] | null;
  failed: boolean;
  loadMore: () => void;
  retry: () => void;
}

/**
 * @param key the settled search's key (`queryKey`): the JSON of its `ModelQuery`
 * @param enabled false while there is nothing to search
 */
export function useModelSearch(key: string, enabled: boolean, pageSize = SEARCH_PAGE_SIZE): ModelSearch {
  const query = useMemo(() => JSON.parse(key) as ModelQuery, [key]);
  const [attempt, setAttempt] = useState(0);
  const [answer, setAnswer] = useState<Answer | null>(null);
  const [moreFor, setMoreFor] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    queryModels(query, { skip: 0, limit: pageSize }).then(
      (page) => {
        if (alive) setAnswer({ key, attempt, results: page.data, hasMore: page.hasMore, problems: null, failed: false });
      },
      (err: unknown) => {
        if (!alive) return;
        const refused = err instanceof InvalidModelQueryError;
        setAnswer({
          key, attempt, results: [], hasMore: false,
          problems: refused ? explainProblems(err.problems) : null, failed: !refused,
        });
      },
    );
    return () => { alive = false; };
  }, [key, query, enabled, attempt, pageSize]);

  const current = answer?.key === key && answer.attempt === attempt ? answer : null;

  const loadMore = useCallback(() => {
    if (!current?.hasMore || moreFor === key) return;
    const skip = current.results.length;
    setMoreFor(key);
    queryModels(query, { skip, limit: pageSize }).then(
      (page) => setAnswer((prev) => (prev?.key === key && prev.results.length === skip
        ? { ...prev, results: [...prev.results, ...page.data], hasMore: page.hasMore }
        : prev)),
      () => undefined,
    ).finally(() => setMoreFor((m) => (m === key ? null : m)));
  }, [current, moreFor, key, query, pageSize]);

  const retry = useCallback(() => setAttempt((a) => a + 1), []);

  return {
    results: current?.results ?? [],
    // Paging is a skip over every row before it: the server stops at 10000.
    hasMore: !!current && current.hasMore && current.results.length <= MAX_QUERY_SKIP,
    searching: enabled && !current,
    loadingMore: moreFor === key,
    problems: current?.problems ?? null,
    failed: current?.failed ?? false,
    loadMore,
    retry,
  };
}
